import {EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal} from '@angular/core';
import {Router} from "@angular/router";
import {CustomerService} from "./customer.service";
import {ModuleConfigurationService} from './module-configuration.service';
import {environment} from '../../../environments/environment';
import {ApiService} from './api.service';
import {TokenService} from './token.service';

@Injectable({
  providedIn: 'root'
})
export class RouterService {
  private router: Router = inject(Router);
  private customerService: CustomerService = inject(CustomerService);
  private moduleConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private apiService: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);
  private readonly postLoginRedirectStorageKey: string = 'postLoginRedirectUrl';

  private currentTargetSignal: WritableSignal<string> = signal<string>('');
  public readonly currentTarget: Signal<string> = this.currentTargetSignal.asReadonly();
  private startNavigationRequest: WritableSignal<number> = signal<number>(0);

  public navigateTo(target: string): void {
    const normalizedTarget = this.normalizeAngularTarget(target);

    // Check if the target route belongs to a legacy module
    const legacyModule = this.findLegacyModuleByRoute(normalizedTarget);

    if (legacyModule) {
      this.openLegacyRoute(normalizedTarget);
      return;
    }

    // Proceed with normal Angular navigation
    const navigationPromise = normalizedTarget.startsWith('/')
      ? this.router.navigateByUrl(normalizedTarget)
      : this.router.navigate([normalizedTarget], {});

    navigationPromise.then(
      (didNavigate) => {
        if (didNavigate) {
          console.log('Navigated to ' + normalizedTarget);
          this.currentTargetSignal.set(normalizedTarget);
          return;
        }

        console.log('Navigation was canceled or skipped for ' + normalizedTarget);
      },
      (error) => {
        console.log('Error navigating to ' + normalizedTarget);
        console.error(error);
      }
    );
  }

  private normalizeAngularTarget(target: string): string {
    const trimmedTarget = target.trim();
    if (!trimmedTarget) {
      return trimmedTarget;
    }

    // Convert same-origin absolute URLs to app-relative router targets.
    if (/^https?:\/\//i.test(trimmedTarget)) {
      try {
        const parsed = new URL(trimmedTarget);
        if (parsed.origin === window.location.origin) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch {
        // Keep original target when URL parsing fails.
      }
    }

    return trimmedTarget;
  }

  private findLegacyModuleByRoute(target: string): typeof this.moduleConfigService.legacyModules[number] | undefined {
    const normalizedTarget = target.startsWith('/') ? target : '/' + target;
    return this.moduleConfigService.legacyModules.find(module =>
      normalizedTarget.startsWith(module.route)
    );
  }

  private buildLegacyUrl(targetRoute: string, token: string): string {
    const customerKey = this.customerService.getCustomerKey();
    if (!customerKey) {
      throw new Error('Missing customer key for legacy redirect');
    }

    const normalizedRoute = targetRoute.startsWith('/') ? targetRoute : '/' + targetRoute;
    const baseUrl = environment.legacyFrontend.url;
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';

    const legacyUrl = new URL(`user-switch/sso/${encodeURIComponent(customerKey)}/${encodeURIComponent(token)}`, cleanBaseUrl);
    legacyUrl.searchParams.set('route', normalizedRoute);

    return legacyUrl.toString();
  }

  public openLegacyRoute(targetRoute: string, target = '_blank'): void {
  // 1. Open the blank tab immediately to preserve the user gesture context
  const newTab = window.open('about:blank', target);

  this.apiService.getLegacyToken().subscribe({
    next: ({token}) => {
      try {
        const legacyUrl = this.buildLegacyUrl(targetRoute, token);
        
        // 2. Update the URL of the already opened tab
        if (newTab) {
          newTab.location.href = legacyUrl;
        } else {
          // Fallback if the tab object wasn't successfully created
          window.open(legacyUrl, target);
        }
        console.log('Redirected to legacy frontend: ' + legacyUrl);
      } catch (error) {
        console.error('Error building legacy redirect URL', error);
        // 3. Clean up: Close the blank tab if URL building fails
        if (newTab) {
          newTab.close();
        }
      }
    },
    error: (error) => {
      console.error('Error getting legacy token', error);
      // 4. Clean up: Close the blank tab if the API request fails
      if (newTab) {
        newTab.close();
      }
    }
  });
}

  public getCurrentUrl(): string {
    return this.router.url;
  }

  public setPostLoginRedirect(target: string | null | undefined): void {
    const sanitizedTarget = this.sanitizePostLoginRedirect(target);
    if (!sanitizedTarget) {
      return;
    }

    try {
      sessionStorage.setItem(this.postLoginRedirectStorageKey, sanitizedTarget);
    } catch (error) {
      console.error('Error storing post login redirect target', error);
    }
  }

  private sanitizePostLoginRedirect(target: string | null | undefined): string | null {
    if (!target || !target.startsWith('/')) {
      return null;
    }

    if (target === '/') {
      return null;
    }

    // Avoid redirecting back into auth routes after login.
    if (target.startsWith('/auth') && target !== '/auth/login/notes') {
      return null;
    }

    return target;
  }

  private consumePostLoginRedirect(): string | null {
    try {
      const target = sessionStorage.getItem(this.postLoginRedirectStorageKey);
      if (target) {
        sessionStorage.removeItem(this.postLoginRedirectStorageKey);
      }
      return target;
    } catch (error) {
      console.error('Error reading post login redirect target', error);
      return null;
    }
  }

  private startNavigationEffect: EffectRef = effect(() => {
    const startNavigationRequest = this.startNavigationRequest();
    const startPage = this.customerService.customerResource()?.home?.start_page;
    if(startNavigationRequest > 0 && startPage) {
      this.startNavigationRequest.set(0);
      this.navigateTo(this.moduleConfigService.getRouteForLandingModule(startPage));
    }
  });

  public navigateToStart(): void {
    const postLoginRedirectTarget = this.consumePostLoginRedirect();
    if (postLoginRedirectTarget) {
      this.navigateTo(postLoginRedirectTarget);
      return;
    }

    const startPage = this.customerService.customerResource()?.home?.start_page;
    if (startPage) {
      this.startNavigationRequest.set(0);
      this.navigateTo(this.moduleConfigService.getRouteForLandingModule(startPage));
      return;
    }

    this.startNavigationRequest.update((requestCount) => requestCount + 1);
  }

  public navigateFromRoot(): void {
    const customerKey = this.customerService.getCustomerKey();

    if (!customerKey) {
      this.navigateTo('/auth/customer');
      return;
    }

    if (this.tokenService.hasToken()) {
      this.navigateToStart();
      return;
    }

    this.navigateTo('/auth/sso');
  }

  public openURL(url: string, target = '_blank'): void {
  if (this.checkIfUrlIsCustomerForm(url)) {
    // 1. Open a blank tab immediately within the synchronous user gesture context
    const newTab = window.open('about:blank', target);

    this.apiService.getLegacyToken().subscribe({
      next: ({token}) => {
        const urlWithToken = new URL(url);
        urlWithToken.searchParams.set('token', token);
        
        // 2. Update the URL of the already opened tab
        if (newTab) {
          newTab.location.href = urlWithToken.toString();
        } else {
          // Fallback if the tab object was somehow lost or aggressively blocked initially
          window.open(urlWithToken.toString(), target);
        }
      },
      error: (error) => {
        console.error('Error getting legacy token', error);
        
        // 3. Fallback: Update to original URL or close the tab if preferred
        if (newTab) {
          newTab.location.href = url;
          // Alternatively, use: newTab.close(); if you don't want an unauthorized page open
        } else {
          window.open(url, target);
        }
      }
    });
  } else {
    // Non-customer forms open instantly without async delay
    window.open(url, target);
  }
}

  public checkIfUrlIsCustomerForm(url: string): boolean {
    const customerFormHostname = this.sanitizeHostname(environment.customerForms.domain);
    const targetHostname = this.sanitizeHostname(url);

    return !!customerFormHostname && !!targetHostname && targetHostname === customerFormHostname;
  }

  private sanitizeHostname (value: string): string | null {
    try {
      return new URL(value).hostname.replace(/^www\./i, '');
    } catch {
      return null;
    }
  };
}
